import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, combineLatest, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap, takeUntil, tap } from 'rxjs';
import { LicenseService } from '../../../core/services/license.service';
import {
  LicensePapFeature,
  LicenseService as CoreLicenseService,
  LicenseProductType,
} from '@isophi/core-legacy';
import { RouterLinkFactory } from '../../../core/router/router-link.factory';
import { KindergartenService } from '@app/core/services/kindergarten.service';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const EXPIRING_WARNING_DAYS = 30;

type LicenseBannerType = 'trial' | 'trial-expired' | 'license-expiring' | 'license-expired';

interface LicenseBannerState {
  type: LicenseBannerType;
  daysLeft?: number;
}

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly licenseService = inject(LicenseService);
  private readonly coreLicenseService = inject(CoreLicenseService);
  private readonly router = inject(Router);
  private readonly routerLinkFactory = inject(RouterLinkFactory);
  private readonly kindergartenService = inject(KindergartenService);
  private readonly document = inject(DOCUMENT);

  private readonly ngUnsubscribe$ = new Subject<void>();

  licenses$: Observable<{ premium: boolean }>;
  licenseBanner: LicenseBannerState | null = null;
  showPapLockOverlay = false;
  isPapLockCheckPending = true;
  isCurrentRouteLockable = false;
  private isPapLocked = false;
  private currentUrl = '';
  private readonly lockedPapRoutes = [
    this.routerLinkFactory.dashboard().join('/'),
    this.routerLinkFactory.plan().join('/'),
    this.routerLinkFactory.portfolio().join('/'),
    this.routerLinkFactory.result().join('/'),
    this.routerLinkFactory.recommendations().join('/'),
  ];

  constructor() {
    this.licenses$ = combineLatest({
      premium: this.licenseService.hasPremium(),
    });
  }

  ngOnInit(): void {
    if (!this.kindergartenService.loggedKindergarten) return;
    combineLatest([
      this.coreLicenseService.canSeePap,
      this.coreLicenseService.isModuleLocked(LicenseProductType.PAP, LicensePapFeature.ACCESS_PAP, LicensePapFeature.TRIAL),
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([canSeePap, isPapExpired]) => {
        this.isPapLocked = isPapExpired || !canSeePap;
        this.isPapLockCheckPending = false;
        this.updatePapLockState(this.currentUrl || this.router.url);
      });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects || e.url),
        startWith(this.router.url),
        tap((url) => {
          this.currentUrl = url;
          this.updatePapLockState(url);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();

    this.observeLicenseBannerState();
  }

  ngOnDestroy(): void {
    this.setBodyScrollLocked(false);
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  get isDemoKindergarten(): boolean {
    return this.kindergartenService.loggedKindergarten?.isDemo;
  }

  get enableGrayBackground(): boolean {
    return (
      this.router.url.includes(this.routerLinkFactory.result()[0]) || this.router.url.includes(this.routerLinkFactory.recommendations()[0])
    );
  }

  private updatePapLockState(url: string): void {
    const cleanUrl = (url || '').split('?')[0];
    const isLockedRoute = this.lockedPapRoutes.some((prefix) => cleanUrl.startsWith(prefix));
    this.isCurrentRouteLockable = isLockedRoute;
    this.showPapLockOverlay = this.isPapLocked && isLockedRoute;
    this.setBodyScrollLocked(this.showPapLockOverlay);
  }

  private setBodyScrollLocked(locked: boolean): void {
    this.document.body.classList.toggle('pap-lock-scroll-disabled', locked);
  }

  private observeLicenseBannerState(): void {
    combineLatest([
      this.coreLicenseService.getLicenseStatus(LicenseProductType.PAP, LicensePapFeature.TRIAL),
      this.coreLicenseService.getLicenseStatus(LicenseProductType.PAP, LicensePapFeature.ACCESS_PAP),
    ])
      .pipe(
        map(([trialStatus, paidStatus]) => this.computeBannerState(trialStatus, paidStatus)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((bannerState) => {
        this.licenseBanner = bannerState;
      });
  }

  private computeBannerState(trialStatus: any, paidStatus: any): LicenseBannerState | null {
    const now = new Date();

    const trialActive = trialStatus?.active || false;
    const trialDaysLeft = trialStatus?.daysUntilEnd || 0;
    const trialValidTo = trialStatus?.validTo ? new Date(trialStatus.validTo) : null;
    const trialExpired = trialValidTo ? now.getTime() > trialValidTo.getTime() : false;

    const paidValidTo = paidStatus?.validTo ? new Date(paidStatus.validTo) : null;
    const paidActive = paidStatus?.active || false;
    const daysToExpiration = paidValidTo ? Math.ceil((paidValidTo.getTime() - now.getTime()) / ONE_DAY_MS) : null;
    const paidExpired = paidValidTo ? now.getTime() > paidValidTo.getTime() : false;
    const paidExpiring = paidActive && daysToExpiration > 0 && daysToExpiration <= EXPIRING_WARNING_DAYS;

    const hasSeparatePaidLicense = paidValidTo && trialValidTo
      ? paidValidTo.getTime() > trialValidTo.getTime()
      : !!(paidValidTo && !trialValidTo);

    // 1. Trial active → show trial countdown
    if (trialActive && trialDaysLeft) {
      return { type: 'trial', daysLeft: trialDaysLeft };
    }

    // 2. Paid license expiring → show license-expiring
    if (paidExpiring && hasSeparatePaidLicense) {
      return { type: 'license-expiring', daysLeft: daysToExpiration };
    }

    // 3. Paid license expired (separate from trial) → show license-expired
    if (paidExpired && hasSeparatePaidLicense) {
      return { type: 'license-expired' };
    }

    // 4. Trial expired, no separate paid license → show trial-expired
    if (trialExpired && !hasSeparatePaidLicense) {
      return { type: 'trial-expired' };
    }

    return null;
  }
}
